import { type ReactNode, useCallback } from 'react';
import type { Lens } from '@hookform/lenses';
import { useFieldArray } from '@hookform/lenses/rhf';
import { useWatch } from 'react-hook-form';
import { z } from 'zod/v4';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { Button } from '@interxfi/design-system/components/button/button';
import { PlainIconButton } from '@interxfi/design-system/components/button/plain-icon/plain-icon-button';
import { einMask } from '@interxfi/design-system/components/input';
import { Typography } from '@interxfi/design-system/components/typography';
import DeleteIcon from '@interxfi/design-system/icons/react/icon-delete';
import PlusIcon from '@interxfi/design-system/icons/react/icon-plus';
import { AppSwitchInputWithLens } from '../../../../components/form/app-switch-input';
import { AppTextInputWithLens } from '../../../../components/form/app-text-input';
import { getOptionalSchema, OptionalField, type OptionalFieldWrapper } from '../../../../components/form/optional-field';
import {
  AddressForm,
  type AddressFormLabels,
  type AddressFormValues,
  addressFormSchema,
  defaultAddressFormValues,
  STACKED_ADDRESS_FORM_LAYOUT,
} from '../../../registration/shared-form/address/address-form';

export const programCorrespondentFormSchema = z.object({
  legalName: z.string().nonempty({ error: 'Legal Name is required' }),
  dba: z.string(),
  webDomains: z.array(z.object({ url: z.url({ error: 'A Web Domain must be a valid URL, starting with http:// or https://' }).or(z.literal('')) })),
  taxIdentifier: z
    .string()
    .nonempty({ error: 'Tax ID / EIN is required' })
    .regex(/^\d{2}-\d{7}$/, 'Tax ID / EIN must be in the format XX-XXXXXXX'),
  mainOfficeAddress: addressFormSchema,
  mailingAddress: getOptionalSchema(addressFormSchema),
  mailingAddressIsSameAsMainOffice: z.boolean(),
});

const MAIN_OFFICE_ADDRESS_LABELS: AddressFormLabels = {
  usAddress: {
    street1: 'Main Office Address',
  },
};

const MAILING_ADDRESS_LABELS: AddressFormLabels = {
  usAddress: {
    street1: 'Mailing Address',
  },
};

type ProgramCorrespondentInformationFormValues = z.infer<typeof programCorrespondentFormSchema>;

export const defaultProgramCorrespondentInformationFormValues: ProgramCorrespondentInformationFormValues = {
  legalName: '',
  dba: '',
  webDomains: [{ url: '' }],
  taxIdentifier: '',
  mainOfficeAddress: defaultAddressFormValues,
  mailingAddress: { isProviding: false, value: undefined },
  mailingAddressIsSameAsMainOffice: false,
};

interface ProgramCorrespondentInformationFormProps {
  lens: Lens<ProgramCorrespondentInformationFormValues>;
  registrationTypeDataFields?: ReactNode;
}

export function ProgramCorrespondentInformationForm({ lens, registrationTypeDataFields }: ProgramCorrespondentInformationFormProps) {
  const { append, fields, remove } = useFieldArray(lens.focus('webDomains').interop());
  const handleAppend = useCallback(() => {
    append({ url: '' });
  }, [append]);
  const mailingAddressIsSameAsMainOffice = useWatch({ ...lens.focus('mailingAddressIsSameAsMainOffice').interop(), defaultValue: false });

  return (
    <VerticalStack gap={4}>
      <Typography.Level3>Correspondent Information</Typography.Level3>

      <VerticalStack gap={7}>
        <VerticalStack gap={4}>
          <AppTextInputWithLens label="Legal Name" lens={lens.focus('legalName')} required />

          <VerticalStack gap={4}>
            {lens.focus('webDomains').map(fields, (field, l, i) => (
              <AppTextInputWithLens
                key={field.id}
                label="Web Domain"
                lens={l.focus('url')}
                required={i === 0}
                suffix={
                  <PlainIconButton
                    aria-label={`Remove web domain at field number: ${i + 1}`}
                    onClick={() => {
                      remove(i);
                    }}
                    tone="danger-dk"
                  >
                    <DeleteIcon aria-hidden height={16} />
                  </PlainIconButton>
                }
              />
            ))}

            <Button affixIcon={<PlusIcon aria-hidden />} onClick={handleAppend} size="medium" type="button" variant="light">
              Add a Web Domain
            </Button>
          </VerticalStack>

          <AppTextInputWithLens label="DBA" lens={lens.focus('dba')} />

          <AppTextInputWithLens label="Tax ID / EIN" lens={lens.focus('taxIdentifier')} maskOptions={einMask} />

          {registrationTypeDataFields}
        </VerticalStack>

        <AddressForm labels={MAIN_OFFICE_ADDRESS_LABELS} layout={STACKED_ADDRESS_FORM_LAYOUT} lens={lens.focus('mainOfficeAddress')} />

        <OptionalField<AddressFormValues>
          defaultValue={defaultAddressFormValues}
          label="Provide Mailing Address"
          lens={lens.focus('mailingAddress').narrow<OptionalFieldWrapper<AddressFormValues>>()}
        >
          <AppSwitchInputWithLens label="Same as main office address" lens={lens.focus('mailingAddressIsSameAsMainOffice')} />

          {mailingAddressIsSameAsMainOffice ? null : (
            <AddressForm labels={MAILING_ADDRESS_LABELS} layout={STACKED_ADDRESS_FORM_LAYOUT} lens={lens.focus('mailingAddress.value').defined()} />
          )}
        </OptionalField>
      </VerticalStack>
    </VerticalStack>
  );
}
